<template>
  <div class="dataset-detail">
    <div class="header">
      <button @click="$router.back()" class="btn-back">← 戻る</button>
      <h1>データセット詳細</h1>
    </div>

    <!-- ローディング -->
    <div v-if="datasetStore.currentDatasetLoading" class="loading">
      読み込み中...
    </div>

    <!-- エラー表示 -->
    <div v-if="datasetStore.currentDatasetError" class="error">
      {{ datasetStore.currentDatasetError }}
    </div>

    <!-- データセット詳細 -->
    <div v-if="dataset" class="content">
      <div class="info-card">
        <h2>{{ dataset.name }}</h2>
        <p class="description">{{ dataset.description || '説明なし' }}</p>

        <div class="meta-grid">
          <div class="meta-item">
            <label>データセットID</label>
            <code>{{ dataset.dataset_id }}</code>
          </div>

          <div class="meta-item">
            <label>検索単価</label>
            <strong class="price">${{ formatPrice(dataset.price_per_search) }}</strong>
          </div>

          <div class="meta-item">
            <label>種別</label>
            <span class="badge">
              {{ dataset.is_platform_dataset ? 'プラットフォーム提供' : 'データプロバイダー提供' }}
            </span>
          </div>

          <div class="meta-item">
            <label>作成日</label>
            <span>{{ formatDate(dataset.created_at) }}</span>
          </div>
        </div>

        <div class="actions">
          <button @click="showPriceModal = true" class="btn-primary">
            価格変更
          </button>
          <button @click="showBulkImportModal = true" class="btn-secondary">
            POI一括インポート
          </button>
          <button @click="confirmDelete" class="btn-danger">
            データセット削除
          </button>
        </div>
      </div>

      <!-- Stripe情報 -->
      <div v-if="dataset.stripe_product_id" class="info-card">
        <h3>Stripe連携情報</h3>
        <div class="meta-grid">
          <div class="meta-item">
            <label>Product ID</label>
            <code>{{ dataset.stripe_product_id }}</code>
          </div>

          <div class="meta-item">
            <label>Meter ID</label>
            <code>{{ dataset.stripe_meter_id }}</code>
          </div>
        </div>
      </div>

      <!-- 価格履歴 -->
      <div v-if="dataset.pricing_history && dataset.pricing_history.length > 0" class="info-card">
        <h3>価格変更履歴</h3>
        <table class="history-table">
          <thead>
            <tr>
              <th>価格</th>
              <th>適用開始</th>
              <th>適用終了</th>
              <th>変更理由</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(history, index) in dataset.pricing_history" :key="index">
              <td>${{ formatPrice(history.price) }}</td>
              <td>{{ formatDateTime(history.effective_from) }}</td>
              <td>{{ history.effective_until ? formatDateTime(history.effective_until) : '現在も有効' }}</td>
              <td>{{ history.reason || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- POI一覧 -->
      <div class="info-card">
        <div class="poi-header">
          <h3>POI一覧</h3>
          <button @click="showCreatePoiModal = true" class="btn-primary">
            POI登録
          </button>
        </div>

        <!-- 検索フィルター -->
        <div class="search-box">
          <input
            v-model="poiKeyword"
            type="text"
            placeholder="POI名で検索..."
            class="search-input"
            @keyup.enter="searchPois"
          />
          <button @click="searchPois" class="btn-search">
            検索
          </button>
        </div>

        <!-- ローディング -->
        <div v-if="poiStore.loading" class="loading-small">
          読み込み中...
        </div>

        <!-- POIテーブル -->
        <div v-else-if="poiStore.pois.length > 0">
          <table class="poi-table">
            <thead>
              <tr>
                <th>POI ID</th>
                <th>名前</th>
                <th>住所</th>
                <th>緯度</th>
                <th>経度</th>
                <th>作成日</th>
                <th>アクション</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="poi in poiStore.pois" :key="poi.poi_id">
                <td><code>{{ poi.poi_id }}</code></td>
                <td>{{ poi.name }}</td>
                <td>{{ poi.address || '-' }}</td>
                <td>{{ poi.latitude?.toFixed(6) }}</td>
                <td>{{ poi.longitude?.toFixed(6) }}</td>
                <td>{{ formatDate(poi.created_at) }}</td>
                <td>
                  <button @click="viewPoiDetail(poi)" class="btn-icon" title="詳細">
                    📄
                  </button>
                  <button @click="confirmDeletePoi(poi)" class="btn-icon" title="削除">
                    🗑️
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- ページネーション -->
          <div v-if="poiStore.totalPages > 1" class="pagination">
            <button
              @click="poiStore.prevPage()"
              :disabled="poiStore.currentPage === 1"
              class="pagination-btn"
            >
              ← 前
            </button>
            <span class="pagination-info">
              {{ poiStore.currentPage }} / {{ poiStore.totalPages }} ページ (全 {{ poiStore.total }} 件)
            </span>
            <button
              @click="poiStore.nextPage()"
              :disabled="poiStore.currentPage === poiStore.totalPages"
              class="pagination-btn"
            >
              次 →
            </button>
          </div>
        </div>

        <!-- 空状態 -->
        <div v-else class="empty-state">
          <p>POIが登録されていません</p>
          <button @click="showCreatePoiModal = true" class="btn-primary">
            POI登録
          </button>
        </div>
      </div>
    </div>

    <!-- 価格変更モーダル -->
    <ChangePriceModal
      v-if="showPriceModal && dataset"
      :dataset="dataset"
      @close="showPriceModal = false"
      @changed="onPriceChanged"
    />

    <!-- POI一括インポートモーダル -->
    <BulkImportModal
      v-if="showBulkImportModal && dataset"
      :dataset-id="dataset.dataset_id"
      @close="showBulkImportModal = false"
      @imported="onImported"
    />

    <!-- POI登録モーダル -->
    <CreatePoiModal
      v-if="showCreatePoiModal && dataset"
      :dataset-id="dataset.dataset_id"
      @close="showCreatePoiModal = false"
      @created="onPoiCreated"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDatasetStore } from '../../stores/dataset.js'
import { usePoiStore } from '../../stores/poi.js'
import ChangePriceModal from '../../components/dataset/ChangePriceModal.vue'
import BulkImportModal from '../../components/dataset/BulkImportModal.vue'
import CreatePoiModal from '../../components/poi/CreatePoiModal.vue'

const route = useRoute()
const router = useRouter()
const datasetStore = useDatasetStore()
const poiStore = usePoiStore()

const showPriceModal = ref(false)
const showBulkImportModal = ref(false)
const showCreatePoiModal = ref(false)
const selectedPoi = ref(null)

const poiKeyword = ref('')

const dataset = computed(() => datasetStore.currentDataset)

/**
 * Format unit price (4 decimals for USD sub-cent pricing)
 * @param {number} price - Price value
 * @returns {string} Formatted price like "0.0010"
 */
function formatPrice(price) {
  return parseFloat(price).toFixed(4)
}

/**
 * Format total amount (2 decimals for USD)
 * @param {number} value - Amount value
 * @returns {string} Formatted amount like "12.34"
 */
function formatTotalAmount(value) {
  return parseFloat(value).toFixed(2)
}

function formatDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleDateString('ja-JP')
}

function formatDateTime(dateString) {
  const date = new Date(dateString)
  return date.toLocaleString('ja-JP')
}

function onPriceChanged() {
  showPriceModal.value = false
  // データを再取得
  datasetStore.fetchDataset(route.params.datasetId)
}

function onImported() {
  showBulkImportModal.value = false
  alert('POIの一括インポートが完了しました')
  // POI一覧を再読み込み
  searchPois()
}

async function confirmDelete() {
  if (!confirm('本当にこのデータセットを削除しますか？この操作は取り消せません。')) {
    return
  }

  try {
    await datasetStore.deleteDataset(dataset.value.dataset_id)
    alert('データセットを削除しました')
    router.push('/datasets')
  } catch (error) {
    alert('データセットの削除に失敗しました')
  }
}

// POI管理関数
async function searchPois() {
  if (!dataset.value) return

  poiStore.selectDataset(dataset.value.dataset_id)
  poiStore.setKeyword(poiKeyword.value)
  await poiStore.searchPois()
}

function viewPoiDetail(poi) {
  selectedPoi.value = poi
  // TODO: POI詳細モーダルを表示
  alert(`POI詳細: ${poi.name}\nPOI ID: ${poi.poi_id}`)
}

async function confirmDeletePoi(poi) {
  if (!confirm(`本当に「${poi.name}」を削除しますか？`)) {
    return
  }

  try {
    await poiStore.deletePoi(poi.poi_id)
    alert('POIを削除しました')
    // 一覧を再読み込み
    await searchPois()
  } catch (error) {
    alert(`POIの削除に失敗しました: ${error.message}`)
  }
}

function onPoiCreated() {
  showCreatePoiModal.value = false
  alert('POIを登録しました')
  // 一覧を再読み込み
  searchPois()
}

onMounted(async () => {
  const datasetId = route.params.datasetId
  await datasetStore.fetchDataset(datasetId)

  // データセット読み込み後にPOI一覧を取得
  if (dataset.value) {
    await searchPois()
  }
})

// データセットが変わったらPOI一覧を再読み込み
watch(() => dataset.value?.dataset_id, async (newDatasetId) => {
  if (newDatasetId) {
    await searchPois()
  }
})
</script>

<style scoped>
.dataset-detail {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.btn-back {
  background: none;
  border: 1px solid #dee2e6;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.btn-back:hover {
  background-color: #f8f9fa;
}

.loading,
.error {
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
}

.error {
  color: #dc3545;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.info-card {
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 2rem;
}

.info-card h2 {
  margin: 0 0 1rem 0;
  font-size: 1.75rem;
}

.info-card h3 {
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  color: #495057;
}

.description {
  color: #6c757d;
  margin-bottom: 2rem;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.meta-item label {
  display: block;
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.meta-item code {
  background-color: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.price {
  font-size: 1.5rem;
  color: #28a745;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: #e9ecef;
  border-radius: 4px;
  font-size: 0.9rem;
}

.actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #c82333;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table th,
.history-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}

.history-table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.history-table tbody tr:hover {
  background-color: #f8f9fa;
}

.poi-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.search-box {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.search-input {
  flex: 1;
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 1rem;
}

.btn-search {
  padding: 0.5rem 1.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.btn-search:hover {
  background-color: #0056b3;
}

.loading-small {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.poi-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1.5rem;
}

.poi-table th,
.poi-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}

.poi-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
}

.poi-table tbody tr:hover {
  background-color: #f8f9fa;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.25rem 0.5rem;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.btn-icon:hover {
  opacity: 1;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f8f9fa;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.9rem;
  color: #6c757d;
}

.empty-state {
  text-align: center;
  padding: 3rem 2rem;
  color: #6c757d;
}

.empty-state p {
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}
</style>
